import { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { CheckCircle2, Phone, Mail } from 'lucide-react';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { API, AGENT, INTEREST_OPTIONS } from './data';

const fieldClass = "bg-transparent border-0 border-b border-white/35 rounded-none px-0 py-3 text-white text-base placeholder:text-white/45 focus-visible:ring-0 focus-visible:border-[#d6cdb4]";
const labelClass = "text-[#e3dcc6] text-xs tracking-[0.12em] uppercase";

const Field = ({ id, label, children }) => (
  <div className="space-y-1">
    <Label htmlFor={id} className={labelClass}>{label}</Label>
    {children}
  </div>
);

export const AgentContact = ({ prefix }) => (
  <div className="space-y-2 text-sm">
    <a href={`tel:${AGENT.phone.replace(/\s/g, '')}`} className="flex items-center gap-3 text-white/85 hover:text-white" data-testid={`${prefix}-agent-phone`}>
      <Phone size={14} />{AGENT.phone}
    </a>
    <a href={`mailto:${AGENT.email}`} className="flex items-center gap-3 text-white/85 hover:text-white" data-testid={`${prefix}-agent-email`}>
      <Mail size={14} />{AGENT.email}
    </a>
  </div>
);

const Success = ({ prefix, onReset }) => (
  <div className="py-6" data-testid={`${prefix}-success`} role="status">
    <CheckCircle2 className="text-[#d6cdb4] mb-4" size={28} />
    <p className="text-white text-xl font-light mb-3">Thank you — your enquiry has been sent.</p>
    <p className="text-white/80 text-sm leading-relaxed mb-6">
      {AGENT.name} of {AGENT.company} will be in touch shortly. If it's urgent, contact her directly:
    </p>
    <AgentContact prefix={`${prefix}-success`} />
    {onReset && (
      <button type="button" onClick={onReset} className="mt-8 text-[#d6cdb4] text-sm underline underline-offset-4" data-testid={`${prefix}-reset`}>
        Send another enquiry
      </button>
    )}
  </div>
);

export const EnquiryForm = ({ prefix, source, interest, showInterest, submitLabel, onDone }) => {
  const empty = { name: '', email: '', phone: '', message: '', interest: interest || 'general' };
  const [form, setForm] = useState(empty);
  const [status, setStatus] = useState('idle');
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      await axios.post(`${API}/enquiry`, { ...form, source_page: source });
      setStatus('sent');
      setForm(empty);
      onDone?.();
    } catch (err) {
      setStatus('error');
    }
  };

  if (status === 'sent') return <Success prefix={prefix} onReset={() => setStatus('idle')} />;

  return (
    <form onSubmit={submit} className="space-y-6" data-testid={`${prefix}-form`}>
      <Field id={`${prefix}-name`} label="Name *">
        <Input id={`${prefix}-name`} value={form.name} onChange={set('name')} className={fieldClass} data-testid={`${prefix}-name`} required autoComplete="name" />
      </Field>
      <Field id={`${prefix}-email`} label="Email *">
        <Input id={`${prefix}-email`} type="email" value={form.email} onChange={set('email')} className={fieldClass} data-testid={`${prefix}-email`} required autoComplete="email" />
      </Field>
      <Field id={`${prefix}-phone`} label="Phone (optional)">
        <Input id={`${prefix}-phone`} type="tel" value={form.phone} onChange={set('phone')} className={fieldClass} data-testid={`${prefix}-phone`} autoComplete="tel" />
      </Field>
      {showInterest && (
        <Field id={`${prefix}-interest`} label="I'm interested in">
          <select id={`${prefix}-interest`} value={form.interest} onChange={set('interest')} className={`${fieldClass} w-full bg-[#1a1e1a]`} data-testid={`${prefix}-interest`}>
            {INTEREST_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Field>
      )}
      <Field id={`${prefix}-message`} label="Message (optional)">
        <Textarea id={`${prefix}-message`} value={form.message} onChange={set('message')} rows={3} className={`${fieldClass} resize-none`} data-testid={`${prefix}-message`} />
      </Field>

      {status === 'error' && (
        <div className="border border-red-300/50 bg-red-900/20 p-4 text-sm text-white/90" data-testid={`${prefix}-error`} role="alert">
          Sorry, your enquiry couldn't be sent. Please try again or contact {AGENT.name} directly:
          <div className="mt-3"><AgentContact prefix={`${prefix}-error`} /></div>
        </div>
      )}

      <p className="text-white/70 text-xs leading-relaxed">
        Your details go to {AGENT.name} of {AGENT.company} and NF Construction and are used only to respond to
        your enquiry. See our <Link to="/privacy" className="underline underline-offset-2 hover:text-white" data-testid={`${prefix}-privacy-link`}>privacy notice</Link>.
      </p>

      <Button
        type="submit"
        disabled={status === 'sending'}
        data-testid={`${prefix}-submit`}
        className="w-full sm:w-auto bg-[#c8bfa8] text-[#1a1e1a] px-10 py-6 rounded-none text-sm tracking-[0.12em] uppercase font-medium hover:bg-white transition-colors"
      >
        {status === 'sending' ? 'Sending...' : submitLabel}
      </Button>
    </form>
  );
};
